import { Printer, PrintTemplate } from "@prisma/client";
import roundNumberToSecondDecimal from "utils/client/roundNumberToSecondDecimal";

export interface PrinterCapabilityPapers {
  [key: string]: (number | null)[];
}

interface CheckSizeRatioWithBufferParams {
  printTemplateSizeRatio: number;
  userDefinedPrintSizeRatio: number;
}

/**
  @description We need to account for rounding of user defined ratios not being perfect so this will compare with a buffer of 0.01, 0.02, and 0.03 
    Ex. [361, 180] ends up with a ratio of 2.01 when rounded to the second decimal place 
    We do want this to be used for prints of a ratio of 2.0 
    This will allow us to keep rounding to 2 places (such as 0.67 matching a 2/3 ratio) as well as match non-exact user defined ratios 
*/

const checkSizeRatioWithBuffers = ({ printTemplateSizeRatio, userDefinedPrintSizeRatio }: CheckSizeRatioWithBufferParams) => {
  const buffers = [0.01, 0.02, 0.03];

  for (const buffer of buffers) {
    const userDefinedRatioWithAddedBuffer = roundNumberToSecondDecimal(userDefinedPrintSizeRatio + buffer);
    const userDefinedRatioWithSubtractedBuffer = roundNumberToSecondDecimal(userDefinedPrintSizeRatio - buffer);
    if (printTemplateSizeRatio === userDefinedRatioWithAddedBuffer || printTemplateSizeRatio === userDefinedRatioWithSubtractedBuffer) {
      return true;
    }
  }

  return false;
};

export const filterPrintersByPaperSize = (printers: Printer[], template: PrintTemplate): Printer[] => {
  const printPaper = template.printPaper;

  const printersFilteredByPaper = printers.filter((printer) => {
    const papers =
      printer.capabilities && typeof printer.capabilities === "object" && !Array.isArray(printer.capabilities)
        ? (printer.capabilities.papers as PrinterCapabilityPapers)
        : null;

    if (!papers) return false;

    /* 
        The printer could have the printPaper as one of the keys of it's paper capabilities 
        The printer could also have a key called "User defined" 
          If the printer has "User defined" paper sizing, we will look at the width and height of the "User defined" paper capability 
          If the width and height ratio is the same as the template, we will consider this a valid printer 
          Ex. "User defined": [1016, 508] - this means it can print 2x4 templates as 1016/508 is the same as 4/2 so the ratio will print the same
      */
    const printerHasPaperSizeCapability = papers[printPaper] ? true : false;
    const printerHasUserDefinedPaperCapability = papers["User defined"];

    // if the printer has the paper size as a paper option - it is capable of printing the template
    if (printerHasPaperSizeCapability) return true;

    // if the printer does not have the paper size as a paper option AND does not have a "User defined" capability - we know this printer cannot print the template size
    if (!printerHasUserDefinedPaperCapability) return false;

    // check if the "User defined" width/height ratio is the same as the print template
    const printTemplateSizeRatio = roundNumberToSecondDecimal((template.width || 0) / (template.height || 0));
    const [printerUserDefinedWidth, printerUserDefinedHeight] = printerHasUserDefinedPaperCapability;

    // we need to check the size ratio as both width/height and height/width as our template size might be backwards (2x4 print capability is the same as 4x2 if rotated)
    const printerUserDefinedPrintSizeRatio = roundNumberToSecondDecimal((printerUserDefinedWidth || 0) / (printerUserDefinedHeight || 0));
    const reversePrinterUserDefinedPrintSizeRatio = roundNumberToSecondDecimal((printerUserDefinedHeight || 0) / (printerUserDefinedWidth || 0));

    // if we cannot determine a size ratio (printer or template is missing either width or height) - return false as we couldn't verify the printer is capable of this size
    if (isNaN(printTemplateSizeRatio) || isNaN(printerUserDefinedPrintSizeRatio) || isNaN(reversePrinterUserDefinedPrintSizeRatio)) return false;

    if (printTemplateSizeRatio === printerUserDefinedPrintSizeRatio || printTemplateSizeRatio === reversePrinterUserDefinedPrintSizeRatio) return true;

    // compare with a small buffer in case the user defined ratio is not exact (like [361, 180] ending up as 2.01 instead of 2)
    const printTemplateSizeRatioMatchesUserDefinedRatioWithBuffer = checkSizeRatioWithBuffers({
      printTemplateSizeRatio,
      userDefinedPrintSizeRatio: printerUserDefinedPrintSizeRatio,
    });
    const printTemplateSizeRatioMatchesReverseUserDefinedRatioWithBuffer = checkSizeRatioWithBuffers({
      printTemplateSizeRatio,
      userDefinedPrintSizeRatio: reversePrinterUserDefinedPrintSizeRatio,
    });

    if (printTemplateSizeRatioMatchesUserDefinedRatioWithBuffer || printTemplateSizeRatioMatchesReverseUserDefinedRatioWithBuffer) return true;

    return false;
  });

  return printersFilteredByPaper;
};
